import * as mobx from 'mobx';
import { IConnectionDetailsDTO, IConnectionGroupDTO } from '../../../api/connections.dao';
import { IRoomDetailsDTO } from '../../../api/rooms.dao';
import { ConnectionEntity } from '../abstract-connection.entity';
import { RoomPresentersConnectionsEntity } from './room-presenters-connections.entity';
import { ConnectionVersion } from '../../../constants/common.constants';

type MeetingConnectionsMapType = Record<IConnectionDetailsDTO['meetingConnectionId'], IConnectionDetailsDTO>;
type RoomConnectionGroupsMapType = Record<IConnectionGroupDTO['meetingConnectionId'], IConnectionGroupDTO>;
type PropKeys = 'roomId' | 'connections' | 'defaultConnectionId' | 'presenters' | 'presenterRoleConnectionId' | 'connectionGroups' | 'connectionVersion';
type RoomConnectionsEntityProps = Pick<IRoomDetailsDTO, PropKeys>;

const key = 'defaultConnectionId';
export class RoomConnectionsEntity extends ConnectionEntity<RoomConnectionsEntityProps> {
    public static dtoToProps(dto: IRoomDetailsDTO): RoomConnectionsEntityProps {
        return ['roomId', 'connections', 'defaultConnectionId', 'presenters', 'presenterRoleConnectionId', 'connectionGroups', 'connectionVersion']
            .reduce((props, propKey) => {
                props[propKey] = dto[propKey];
                return props;
            }, {} as RoomConnectionsEntityProps);
    }
    public static create(dto: IRoomDetailsDTO): RoomConnectionsEntity {
        return new RoomConnectionsEntity(RoomConnectionsEntity.dtoToProps(dto));
    }

    public getId(): number { return this.getProps().roomId; }

    public toDTO(): RoomConnectionsEntityProps {
        const _dto = {
            ...this.presenterConnections.toDTO(),
            connections: this.getConnections()
        };

        if (this.hasAssignedConnection()) {
            _dto[key] = this.getAssignedConnection();
        } else {
            delete _dto[key];
        }

        if (this.getProps().connectionVersion === ConnectionVersion.V2) {
            _dto['connectionGroups'] = this.getConnectionGroups();
        }

        return _dto as RoomConnectionsEntityProps;
    }

    @mobx.observable
    private [key]: {};
    @mobx.observable
    private readonly presenterConnections: RoomPresentersConnectionsEntity;
    @mobx.observable
    private readonly _connections: MeetingConnectionsMapType = {};
    @mobx.observable
    private connectionGroups: RoomConnectionGroupsMapType = {};

    private constructor({ connections, defaultConnectionId, connectionGroups = [], ...rest }: RoomConnectionsEntityProps) {
        super(key, rest);

        if (defaultConnectionId) this.assignConnection(defaultConnectionId);

        connections.forEach(function(item) {
            this[item.meetingConnectionId] = item;
        }, this._connections);

        connectionGroups.forEach(function(item) {
            this[item.meetingConnectionId] = item;
        }, this.connectionGroups);

        this.presenterConnections = RoomPresentersConnectionsEntity.create(rest as IRoomDetailsDTO);
    }

    @mobx.action
    public removeRoomConnectionById(id: number): void {
        this.removeRoomConnectionFromGroups(this._connections[id]);
        delete this._connections[id];
        super.invalidateAssignedConnectionById(id);
        this.roomPresenters.invalidateAssignedConnectionById(id);
    }

    @mobx.action
    public removeRoomConnectionGroupById(id: number): void {
        delete this.connectionGroups[id];
        super.invalidateAssignedConnectionById(id);
        this.roomPresenters.invalidateAssignedConnectionById(id);
    }

    public get roomPresenters(): RoomPresentersConnectionsEntity {
        return this.presenterConnections;
    }

    public getConnections(): IConnectionDetailsDTO[] {
        return Object.values(this._connections);
    }
    @mobx.action
    public setRoomConnection(connection: IConnectionDetailsDTO): void {
        this._connections[connection.meetingConnectionId] = connection;
    }

    @mobx.action
    public setRoomConnectionGroup(connectionGroup: IConnectionGroupDTO): void {
        this.connectionGroups[connectionGroup.meetingConnectionId] = connectionGroup;
    }

    public getConnectionGroups(): IConnectionGroupDTO[] {
        return Object.values(this.connectionGroups);
    }

    @mobx.action
    public removeRoomConnectionFromGroups(connection: IConnectionDetailsDTO): void {
        Object.values(this.connectionGroups).forEach(group => {
            const idx = group.joinMethods.findIndex(jm => jm.joinMethodId === connection.joinMethodId);
            if (idx !== -1) {
                group.joinMethods.splice(idx, 1);
            }
        });
    }
}
